import React, { useRef, useState } from 'react';
import { useTranslate } from 'react-admin';
import { Box, Button, Stack, Typography, useTheme } from '@mui/material';
import {
  CameraAltOutlined as CameraIcon,
  CloseRounded as RemoveIcon,
  CloudUploadOutlined as UploadIcon,
  PictureAsPdfOutlined as PdfIcon,
} from '@mui/icons-material';
import { CameraCapture } from './CameraCapture';

/** A PDF preview is its blob URL behind this prefix; anything else is an image URL. */
export const PDF_PREVIEW_PREFIX = 'pdf:';

/** A chosen file shown as the reader will recognise it: the page of a PDF, or the image. Without
 *  `maxHeight` the image takes the full width it is given. */
export const FilePreview: React.FC<{ preview: string; alt: string; maxHeight?: number }> = ({ preview, alt, maxHeight }) => {
  const theme = useTheme();
  if (preview.startsWith(PDF_PREVIEW_PREFIX)) {
    return (
      <object
        data={preview.slice(PDF_PREVIEW_PREFIX.length)}
        type="application/pdf"
        width="100%"
        style={{ height: maxHeight ? maxHeight + 60 : 400, borderRadius: theme.custom.radius.small, display: 'block' }}
      >
        <Stack alignItems="center" spacing={1} sx={{ py: 3 }}>
          <PdfIcon sx={{ fontSize: 48, color: 'text.secondary' }} />
          <Typography variant="body2" color="text.secondary">PDF</Typography>
        </Stack>
      </object>
    );
  }
  return (
    <Box
      component="img"
      src={preview}
      alt={alt}
      sx={{ maxWidth: '100%', maxHeight: maxHeight ?? 'none', width: maxHeight ? undefined : '100%', borderRadius: theme.custom.radius.small, display: 'block', mx: 'auto' }}
    />
  );
};

interface ImageDropzoneProps {
  label: string;
  preview: string | null;
  /** The `accept` of the file picker. */
  accept: string;
  /** A file picked or dropped; the caller checks its type. */
  onFile: (file: File) => void;
  onRemove: () => void;
  /** A frame captured with the camera, as base64 JPEG. */
  onCapture: (b64: string) => void;
  facingMode: 'environment' | 'user';
  idealWidth: number;
  previewMaxHeight: number;
}

/**
 * Where one image goes in: drop it, click (or press Enter) to pick it, or take it with the camera.
 * Once chosen it shows as a preview with a button to remove it.
 */
export const ImageDropzone: React.FC<ImageDropzoneProps> = ({
  label, preview, accept, onFile, onRemove, onCapture, facingMode, idealWidth, previewMaxHeight,
}) => {
  const t = useTranslate();
  const theme = useTheme();
  const inputRef = useRef<HTMLInputElement>(null);
  const [showCamera, setShowCamera] = useState(false);

  const pick = () => inputRef.current?.click();

  return (
    <Stack spacing={1} sx={{ minWidth: 0, flex: 1 }}>
      <Typography variant="subtitle2">{label}</Typography>
      {showCamera ? (
        <CameraCapture
          facingMode={facingMode}
          idealWidth={idealWidth}
          onClose={() => setShowCamera(false)}
          onCapture={(b64) => { setShowCamera(false); onCapture(b64); }}
        />
      ) : (
        <>
          <input
            ref={inputRef}
            type="file"
            accept={accept}
            hidden
            onChange={(e) => {
              const file = e.target.files?.[0];
              if (file) onFile(file);
              e.target.value = '';
            }}
          />
          <Box
            role="button"
            tabIndex={0}
            aria-label={`${label}: ${t('test.drop_image')}`}
            onClick={pick}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); pick(); }
            }}
            onDragOver={(e) => e.preventDefault()}
            onDrop={(e) => {
              e.preventDefault();
              const file = e.dataTransfer.files[0];
              if (file) onFile(file);
            }}
            sx={{
              border: '2px dashed',
              borderColor: preview ? 'primary.main' : 'divider',
              borderRadius: theme.custom.radius.card,
              backgroundColor: theme.palette.background.default,
              p: preview ? 1 : 2,
              minHeight: 100,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              textAlign: 'center',
              cursor: 'pointer',
              transition: 'border-color 0.2s',
              '&:hover, &:focus-visible': { borderColor: 'primary.main', outline: 'none' },
            }}
          >
            {preview ? (
              <Box sx={{ width: '100%' }}>
                <FilePreview preview={preview} alt={label} maxHeight={previewMaxHeight} />
              </Box>
            ) : (
              <Stack alignItems="center" spacing={0.5}>
                <UploadIcon sx={{ fontSize: 28, color: 'text.secondary' }} />
                <Typography variant="body2" color="text.secondary">{t('test.drop_image')}</Typography>
              </Stack>
            )}
          </Box>
          <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap">
            <Button variant="outlined" startIcon={<CameraIcon />} onClick={() => setShowCamera(true)}>
              {t('test.use_camera')}
            </Button>
            {preview && (
              <Button variant="outlined" startIcon={<RemoveIcon />} onClick={onRemove}>
                {t('screens.testPage.removeImage')}
              </Button>
            )}
          </Stack>
        </>
      )}
    </Stack>
  );
};
